// Centralized date/time formatting for TWM.
//
// Two distinct concerns, deliberately handled differently:
//
//  1. Date-only calendar values (rental "from"/"to", availability, due dates).
//     Booqable anchors these at midnight UTC and they carry no real wall-clock
//     time — they mean a calendar day. Render in UTC so the day is preserved for
//     every viewer; any timezone west of UTC (incl. Puerto Rico, AST/UTC-4) would
//     pull midnight back to the prior day. -> formatCalendarDate
//
//  2. Real timestamps ("created", "modified", "sent", chat times). These are
//     instants; render them in the VIEWER's local timezone — staff in Puerto Rico
//     see AST, a dev in Saskatchewan sees CST. -> formatBusinessDateTime /
//     formatBusinessDate / formatBusinessTime
//
// getDisplayTimeZone is the single seam for case (2): it returns undefined today,
// which makes toLocale* use the runtime's local timezone. When the product goes
// multi-tenant SaaS and timestamps should read in each company's operating zone,
// return that company's timezone here and every call site is already correct.
export const getDisplayTimeZone = (): string | undefined => undefined;

// (1) Calendar day (e.g. "Jun 24, 2026"), rendered in UTC.
// Use for date-only values anchored at midnight UTC (rental periods, availability).
export const formatCalendarDate = (value?: string | null): string => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return typeof value === 'string' ? value : 'N/A';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

// (2) Date + time (e.g. "Jun 24, 2026, 6:00 PM"), in the viewer's local timezone.
// Use for real event timestamps (created, modified, sent, etc.).
export const formatBusinessDateTime = (value?: string | null): string => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: getDisplayTimeZone(),
  });
};

// (3) Date only (e.g. "Jun 24, 2026") for a real timestamp, viewer's local
// timezone. Use when an event instant is shown as a date with no time.
export const formatBusinessDate = (value?: string | null): string => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: getDisplayTimeZone(),
  });
};

// (4) Time only (e.g. "6:00 PM") for a real timestamp, viewer's local timezone.
// Use for chat/message bubbles and other clock-time-only displays.
export const formatBusinessTime = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: getDisplayTimeZone(),
  });
};
